<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue';
import FormLabel from '@shared/components/Form/FormLabel.vue';

export interface Item {
  label: string;
  value: string;
}

interface Props {
  label?: string;
  required?: boolean;
  infoText?: string;
  invalidMessage?: string | null;
  options: Item[];
  customOptionValue?: string;
  customInputPlaceholder?: string;
  addItemButtonText?: string;
  placeholder?: string;
  allowAdd: boolean;
}

const modelValue = defineModel<string>();
const isDisabled = ref(false);
const props = withDefaults(defineProps<Props>(), {
  customOptionValue: 'custom',
  customInputPlaceholder: 'Add custom value',
  addItemButtonText: 'Add',
  placeholder: 'Select an option or create new',
});

const emit = defineEmits(['customOptionAdded']);
const customOptions = ref<Item[]>([]);

const allOptions = computed(() => {
  const combinedOptions = [...customOptions.value, ...props.options];
  
  // Remove duplicates by value
  return combinedOptions.filter((option, index, self) => 
    index === self.findIndex(o => o.value === option.value)
  );
});

const showCustomInput = ref(false);
const customValue = ref('');
const selectEl = ref<HTMLElement | null>(null);
const customInputEl = ref<HTMLInputElement | null>(null);
const dropdownOpen = ref(false);
const selectedIndex = ref(0);

const hasSelection = computed(() => {
  return modelValue.value && modelValue.value.trim() !== '';
});

const selectedText = computed(() => {
  if (!modelValue.value) return '';
  const option = allOptions.value.find(o => o.value === modelValue.value);
  return option ? option.label : '';
});

watch(modelValue, (newValue) => {
  updateCustomInputVisibility();
});

const updateCustomInputVisibility = () => {
  showCustomInput.value = modelValue.value === props.customOptionValue;
  
  if (showCustomInput.value) {
    setTimeout(() => {
      customInputEl.value?.focus();
    }, 0);
  }
};

const handleCustomInputChange = () => {
  if (props.allowAdd && customValue.value.trim()) {
    
    const newOption: Item = {
      label: customValue.value,
      value: customValue.value // Keep as is - do not transform
    };
    
    const exists = allOptions.value.some(option => 
      option.value === newOption.value || option.label === newOption.label
    );
    
    if (!exists) {
      customOptions.value.push(newOption);      
      modelValue.value = newOption.label;      
      emit('customOptionAdded', newOption);
      
      // Reset custom input and hide it
      customValue.value = '';
      showCustomInput.value = false;
    }
  }
};

const getInvalidMessage = () => {
  return props.invalidMessage || '';
};

const toggleDropdown = () => {
  if (isDisabled.value) return;
  dropdownOpen.value = !dropdownOpen.value;
};

const selectItem = (item: Item) => {
  modelValue.value = item.value;
  dropdownOpen.value = false;
  
  // Check if this is the "New..." option
  updateCustomInputVisibility();
};

// Close dropdown when clicking outside
onMounted(() => {
  document.addEventListener('click', (event) => {
    if (dropdownOpen.value && selectEl.value && !selectEl.value.contains(event.target as Node)) {
      dropdownOpen.value = false;
    }
  });
});
</script>

<template>
  <div class="form-select-with-new">
    <FormLabel 
      :label="props.label || 'Select'" 
      :id="'dropdown-select-' + $.uid" 
      :infoText="props.infoText" 
      :required="props.required">
    </FormLabel>
    
    <div class="select-wrapper" ref="selectEl">
      <!-- Dropdown trigger -->
      <div 
        class="form-control input-focus js-adjust-height"
        :class="{ 'has-value': hasSelection, 'open': dropdownOpen }"
        @click="toggleDropdown"
        :tabindex="isDisabled ? -1 : 0"
        :aria-expanded="dropdownOpen"
        :aria-haspopup="true">
        <span v-if="hasSelection" class="selected-value">{{ selectedText }}</span>
        <span v-else class="placeholder">{{ props.placeholder }}</span>
      </div>
      
      <!-- Dropdown -->
      <div class="custom-dropdown" v-show="dropdownOpen">
        <div 
          v-for="(option, index) in allOptions" 
          :key="option.value"
          class="dropdown-item"
          :class="{ 'active': modelValue === option.value }"
          @click="selectItem(option)">
          {{ option.label }}
        </div>
      </div>
    </div>
    
    <div v-if="allowAdd && showCustomInput" class="custom-input-container mt-2">
      <div class="input-group">
        <input
          ref="customInputEl"
          type="text"
          class="form-control"
          :placeholder="props.customInputPlaceholder"
          v-model="customValue"
          @keyup.enter="handleCustomInputChange"
        />
        <div class="input-group-append">
          <button 
            class="btn btn-primary" 
            type="button" 
            @click="handleCustomInputChange"
            :disabled="!customValue.trim()">
            {{ props.addItemButtonText }}
          </button>
        </div>
      </div>
    </div>
    
    <div class="invalid-feedback m-xs-t" v-if="props.invalidMessage">
      <p>{{ getInvalidMessage() }}</p>
    </div>
  </div>
</template>

<style scoped> 
  .form-select-with-new {
    margin-bottom: 1rem;
  }

  .select-wrapper {
    position: relative;
  }

  .placeholder {
    color: var(--input-placeholder-color, #6c757d) !important;
    pointer-events: none;
  }

  .custom-select-display {
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23343a40' viewBox='0 0 12 12'%3E%3Cpath d='M6 9L12 3H0z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.75rem center;
    background-size: 8px 10px;
  }

  .custom-select-display:focus {
    border-color: var(--brand-primary-500);
    box-shadow: 0 0 0 0.2rem rgba(var(--brand-primary-500-rgb), 0.25);
    outline: none;
  }

  .custom-dropdown {
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    z-index: 1000;
    max-height: 250px;
    overflow-y: auto;
    background-color: white;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  }

  .dropdown-item {
    padding: 0.5rem 1rem;
    cursor: pointer;
  }

  .dropdown-item:hover, .dropdown-item.active {
    background-color: var(--brand-primary-500);
    color: white;
  }

  .has-value {
    border-color: var(--brand-primary-500);
  }

  .custom-input-container {
    transition: all 0.3s ease;
    margin-top: 0.5rem;
  }

  .input-group {
    display: flex;
    flex-wrap: nowrap;
  }

  .input-group-append {
    display: flex;
    margin-left: 0.5rem;
  }

  .input-group-append button {
    border-radius: 0.25rem;
  }

  .custom-input-container input {
    border-radius: 0.25rem;
  }

  .custom-input-container input:focus {
    border-color: var(--brand-primary-500);
    box-shadow: 0 0 0 0.2rem rgba(var(--brand-primary-500-rgb), 0.25);
    position: relative;
    z-index: 2;
  }
</style> 

